import clsx from "clsx";
import React, { useState } from "react";

import Button from "@material-ui/core/Button";
import { Theme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Comment from "components/Comments/Comment";
import { ICommentData } from "components/Comments/Comment/Comment";
import { ECommentDisplayingVariant } from "components/Comments/Comment/helper";
import CommentsHeader from "components/Comments/CommentsHeader";
import Loader from "components/Loader";
import Pagination from "components/Pagination";
import HorseFOTComment from "./HorseFOTComment";
import HorseGeneralVetComment from "./HorseGeneralVetComment";
import HorseGeneralVetCommentEditor from "./HorseGeneralVetCommentEditor";

import useStyles from "./styles";

interface Props {
  horse: racehorse360.IHorse;
  commentsList: racehorse360.IHorseAggregatedComment[];
  isCommentsFetching: boolean;
  isFetchingNextPage?: boolean;
  refetchHorseWarningMessage: () => void;
  refetchComments: () => void;
}

const PAGE_SIZE = 5;

const Comments = (props: Props) => {
  const {
    horse,
    isCommentsFetching,
    isFetchingNextPage = false,
    commentsList,
    refetchHorseWarningMessage,
    refetchComments
  } = props;
  const classes = useStyles();
  const matchesUpMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("md")
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [editingCommentId, setEditingCommentId] = useState<string>(null);
  const [isAddingComment, setIsAddingComment] = useState(false);

  const handleAddCommentClick = () => {
    setIsAddingComment(true);
  };

  const handleAddCommentClose = () => {
    setIsAddingComment(false);
  };

  const handleEditHorseGeneralVetCommentStart = (commentId: string) => {
    setEditingCommentId(commentId);
  };

  const handleEditHorseGeneralVetCommentFinish = () => {
    setEditingCommentId(null);
  };

  const renderComment = (
    comment: racehorse360.IHorseAggregatedComment,
    index: number
  ) => {
    switch (comment.commentSourceKind) {
      case racehorse360.CommentSourceKind
        .COMMENT_SOURCE_KIND_HORSE_GENERAL_VET_COMMENT:
        return (
          <HorseGeneralVetComment
            key={comment.id}
            className={clsx(classes.comment, {
              [classes.oddComment]: index % 2
            })}
            isEditing={comment.id === editingCommentId}
            data={comment}
            onEditStart={handleEditHorseGeneralVetCommentStart}
            onEditFinish={handleEditHorseGeneralVetCommentFinish}
            refetchComments={refetchComments}
            refetchHorseWarningMessage={refetchHorseWarningMessage}
          />
        );
      case racehorse360.CommentSourceKind
        .COMMENT_SOURCE_KIND_HORSE_FLAG_ON_TRACK:
        return (
          <HorseFOTComment
            key={comment.id}
            className={clsx(classes.comment, {
              [classes.oddComment]: index % 2
            })}
            data={comment}
          />
        );
      default: {
        const commentData: ICommentData = {
          id: comment.id,
          content: comment.body,
          warningMessage: comment.warningMessage,
          commentIconType: comment.horseAggregatedCommentIconType,
          isRead: comment.isRead,
          eventType: comment.eventType,
          commenterId: comment.commenterId,
          commenterLastName: comment.commenterLastName,
          commenterFirstName: comment.commenterFirstName,
          eventDate: comment.eventDate,
          updatedOn: comment.updatedOn
        };

        return (
          <Comment
            key={comment.id}
            className={clsx(classes.comment, {
              [classes.oddComment]: index % 2
            })}
            shouldDisplayIsReadStatus={false}
            data={commentData}
            variant={ECommentDisplayingVariant.COMMENT_FOR_VETERINARIAN}
          />
        );
      }
    }
  };

  const renderComments = (
    commentsList: racehorse360.IHorseAggregatedComment[]
  ) => {
    if (!commentsList?.length) {
      return (
        <section className={classes.noComments}>
          No Comments for {horse.name}
        </section>
      );
    }

    return commentsList.map(
      (comment: racehorse360.IHorseAggregatedComment, index: number) => {
        return renderComment(comment, index);
      }
    );
  };

  const renderContentUpMd = () => {
    const sliced = commentsList.slice(
      (pageNumber - 1) * PAGE_SIZE,
      (pageNumber - 1) * PAGE_SIZE + PAGE_SIZE
    );

    return (
      <>
        {renderComments(sliced)}

        {commentsList?.length > 0 && (
          <div className={classes.footer}>
            <Pagination
              totalCount={commentsList.length}
              onPageChange={setPageNumber}
              pageSize={PAGE_SIZE}
              pageNumber={pageNumber}
            />
          </div>
        )}
      </>
    );
  };

  const renderContentDownMd = () => {
    return (
      <div className={classes.scrollContainer}>
        {renderComments(commentsList)}
      </div>
    );
  };

  const renderAddCommentBlock = () => {
    if (isAddingComment) {
      return (
        <HorseGeneralVetCommentEditor
          horseId={horse.id}
          className={classes.commentEditor}
          refetchHorseWarningMessage={refetchHorseWarningMessage}
          refetchComments={refetchComments}
          onClose={handleAddCommentClose}
        />
      );
    } else {
      return (
        <div className={classes.emptyComment}>
          <Button
            className={classes.addButton}
            startIcon={<AddCircleOutlineIcon className={classes.addIcon} />}
            onClick={handleAddCommentClick}
          >
            Add Comment
          </Button>
        </div>
      );
    }
  };

  return (
    <div className={clsx(classes.root, { [classes.rootSticky]: matchesUpMd })}>
      <CommentsHeader
        shouldDisplayUnreadCount={false}
        title={matchesUpMd ? "Activity" : "All Comments"}
        hasComments={Boolean(commentsList.length)}
        headerClassName={classes.header}
      />
      <div
        className={clsx(classes.list, { [classes.listSticky]: matchesUpMd })}
      >
        <div className={classes.addCommentBox}>{renderAddCommentBlock()}</div>
        {matchesUpMd ? renderContentUpMd() : renderContentDownMd()}
        <Loader
          className={clsx(classes.infiniteLoader, {
            [classes.showInfiniteLoader]:
              isCommentsFetching || isFetchingNextPage
          })}
        />
      </div>
    </div>
  );
};

export default React.memo(Comments);
